/** Comprobaciones previas de compatibilidad (navegador objetivo: Chrome/Chromium de escritorio). */

export interface EnvironmentInfo {
  userAgent: string;
  brands: string[];
  isSecureContext: boolean;
  hasGetUserMedia: boolean;
  hasFullscreen: boolean;
  viewportWidth: number;
  viewportHeight: number;
  outerWidth: number;
  devicePixelRatio: number;
}

export interface Check {
  id: string;
  label: string;
  ok: boolean;
  /** `true` si impide continuar con cámara real. */
  blocking: boolean;
  detail?: string;
}

export function readEnvironment(): EnvironmentInfo {
  const uaData = (navigator as Navigator & { userAgentData?: { brands?: Array<{ brand: string }> } }).userAgentData;
  return {
    userAgent: navigator.userAgent,
    brands: uaData?.brands?.map((b) => b.brand) ?? [],
    isSecureContext: window.isSecureContext,
    hasGetUserMedia: typeof navigator.mediaDevices?.getUserMedia === "function",
    hasFullscreen: typeof document.documentElement.requestFullscreen === "function",
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    outerWidth: window.outerWidth,
    devicePixelRatio: window.devicePixelRatio,
  };
}

export function isDesktopChromium(env: Pick<EnvironmentInfo, "userAgent" | "brands">): boolean {
  const mobile = /Mobi|Android|iPhone|iPad/i.test(env.userAgent);
  const chromium = env.brands.some((b) => /Chromium|Google Chrome/.test(b)) || (/Chrome\/\d+/.test(env.userAgent) && !/Firefox|FxiOS/.test(env.userAgent));
  return chromium && !mobile;
}

/** Zoom aproximado del navegador; `null` si no es estimable. */
export function estimateZoom(env: Pick<EnvironmentInfo, "outerWidth" | "viewportWidth">): number | null {
  if (!(env.outerWidth > 0) || !(env.viewportWidth > 0)) return null;
  const z = Math.round((env.outerWidth / env.viewportWidth) * 100) / 100;
  return z > 0 && z <= 10 ? z : null;
}

export function runChecks(env: EnvironmentInfo): Check[] {
  const zoom = estimateZoom(env);
  return [
    {
      id: "browser",
      label: "Navegador Chrome/Chromium de escritorio",
      ok: isDesktopChromium(env),
      blocking: false,
      detail: "El estudio está probado en Chrome/Chromium de escritorio; otros navegadores pueden fallar.",
    },
    {
      id: "secure",
      label: "Conexión segura (HTTPS o localhost)",
      ok: env.isSecureContext,
      blocking: true,
      detail: "El navegador solo permite usar la cámara en un contexto seguro.",
    },
    { id: "camera-api", label: "Acceso a cámara disponible", ok: env.hasGetUserMedia, blocking: true },
    { id: "fullscreen-api", label: "Pantalla completa disponible", ok: env.hasFullscreen, blocking: true },
    {
      id: "viewport",
      label: `Ventana suficientemente grande (${env.viewportWidth}×${env.viewportHeight} px)`,
      ok: env.viewportWidth >= 1024 && env.viewportHeight >= 600,
      blocking: false,
      detail: "Se recomienda al menos 1024×600 px.",
    },
    {
      id: "zoom",
      label: zoom === null ? "Zoom del navegador: no estimable" : `Zoom del navegador ≈ ${Math.round(zoom * 100)} %`,
      ok: zoom === null || Math.abs(zoom - 1) < 0.15,
      blocking: false,
      detail: "Usa zoom al 100 % (Ctrl+0) y no lo cambies durante la sesión.",
    },
  ];
}
